/**
 * System Health Tab Component
 * Features: API latency monitoring, scheduled jobs status, cache management, error dashboard
 */
import { useState, useEffect } from 'react';
import { 
    Heart, RefreshCw, Activity, Clock, Database, Mail, Shield, Bell,
    MapPin, AlertTriangle, CheckCircle, XCircle, Loader2, Cpu, Trash2
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/api';
import { toast } from 'sonner';
import { BentoCard, NeonIcon, API_URL } from './shared';

const SystemHealthTab = ({ systemHealth, dnsStatus, healthLoading, fetchSystemHealth, isSpanish }) => {
    const [activeSubTab, setActiveSubTab] = useState('overview');
    const [scheduledJobs, setScheduledJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [apiLatency, setApiLatency] = useState({});
    const [latencyLoading, setLatencyLoading] = useState(false);
    const [cacheStats, setCacheStats] = useState(null);
    const [errorStats, setErrorStats] = useState(null);
    const [clearingCache, setClearingCache] = useState({});
    const [triggeringJob, setTriggeringJob] = useState({});
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy': case 'configured': case 'initialized': case 'success': return 'text-green-400';
            case 'degraded': case 'error': case 'failed': return 'text-red-400';
            default: return 'text-yellow-400';
        }
    };
    
    const getStatusNeonColor = (status) => {
        switch (status) {
            case 'healthy': case 'configured': case 'initialized': case 'success': return 'green';
            case 'degraded': case 'error': case 'failed': return 'red';
            default: return 'yellow';
        }
    };
    
    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy': case 'configured': case 'initialized': case 'success':
                return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'degraded': case 'error': case 'failed':
                return <XCircle className="w-5 h-5 text-red-400" />;
            default:
                return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
        }
    };
    
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const authHeaders = await getAuthHeaders();
                const headers = {
                    ...authHeaders,
                    'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
                };
                const res = await fetch(`${API_URL}/api/admin/scheduled-jobs`, { headers, credentials: 'include' });
                if (res.ok) setScheduledJobs((await res.json()).jobs || []);
            } catch (e) { console.error('Jobs fetch error:', e); }
            finally { setJobsLoading(false); }
        };
        fetchJobs();
    }, []);
    
    const checkApiLatency = async () => {
        setLatencyLoading(true);
        // Use endpoints that support X-Internal-Secret to avoid session conflicts
        const endpoints = [
            { name: '/api/health', path: '/api/health', method: 'GET' },
            { name: '/api/admin/check', path: '/api/admin/check', method: 'GET' },
            { name: '/api/transits/current', path: '/api/transits/current', method: 'GET' }
        ];
        const results = {};
        
        for (const ep of endpoints) {
            const start = performance.now();
            try {
                // Only use X-Internal-Secret header, not session auth
                // This prevents triggering AuthGatekeeper refresh
                const headers = {
                    'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
                };
                
                const res = await fetch(`${API_URL}${ep.path}`, { 
                    method: ep.method, 
                    headers
                    // Intentionally NOT including credentials: 'include' 
                    // to avoid sending stale session cookies
                });
                
                const latency = Math.round(performance.now() - start);
                
                // Consider the endpoint healthy if:
                // - Response is OK (2xx)
                // - Response is 401/403 (auth required but endpoint is reachable)
                // - Response is 405 (method not allowed but endpoint exists)
                const isReachable = res.ok || res.status === 401 || res.status === 403 || res.status === 405;
                
                results[ep.name] = { 
                    status: isReachable ? 'healthy' : 'error', 
                    latency, 
                    httpStatus: res.status,
                    timestamp: new Date().toISOString() 
                };
            } catch (e) {
                results[ep.name] = { 
                    status: 'error', 
                    latency: null, 
                    error: e.message, 
                    timestamp: new Date().toISOString() 
                };
            }
        }
        setApiLatency(results);
        setLatencyLoading(false);
    };
    
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const authHeaders = await getAuthHeaders();
                const headers = {
                    ...authHeaders,
                    'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
                };
                const [cacheRes, errorRes] = await Promise.all([
                    fetch(`${API_URL}/api/admin/cache-stats`, { headers, credentials: 'include' }),
                    fetch(`${API_URL}/api/admin/error-stats`, { headers, credentials: 'include' })
                ]);
                if (cacheRes.ok) setCacheStats(await cacheRes.json());
                if (errorRes.ok) setErrorStats(await errorRes.json());
            } catch (e) { console.error('Stats fetch error:', e); }
        };
        fetchStats();
    }, []);
    
    const handleClearCache = async (cacheType) => {
        setClearingCache(prev => ({ ...prev, [cacheType]: true }));
        try {
            const authHeaders = await getAuthHeaders();
            const headers = {
                ...authHeaders,
                'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
            };
            const res = await fetch(`${API_URL}/api/admin/cache/clear/${cacheType}`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
                toast.success(isSpanish ? `Cache ${cacheType} limpiado` : `${cacheType} cache cleared`);
                const cacheRes = await fetch(`${API_URL}/api/admin/cache-stats`, { headers, credentials: 'include' });
                if (cacheRes.ok) setCacheStats(await cacheRes.json());
            }
        } catch (e) { toast.error(isSpanish ? 'Error al limpiar cache' : 'Failed to clear cache'); }
        finally { setClearingCache(prev => ({ ...prev, [cacheType]: false })); }
    };
    
    const handleTriggerJob = async (jobId) => {
        setTriggeringJob(prev => ({ ...prev, [jobId]: true }));
        try {
            const authHeaders = await getAuthHeaders();
            const headers = {
                ...authHeaders,
                'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
            };
            const res = await fetch(`${API_URL}/api/admin/trigger-job/${jobId}`, { method: 'POST', headers, credentials: 'include' });
            const data = await res.json();
            if (res.ok) toast.success(data.message || (isSpanish ? 'Job ejecutado' : 'Job triggered'));
            else toast.error(data.detail || 'Failed');
        } catch (e) { toast.error(isSpanish ? 'Error al ejecutar job' : 'Failed to trigger job'); }
        finally { setTriggeringJob(prev => ({ ...prev, [jobId]: false })); }
    };
    
    return (
        <div className="space-y-4">
            <BentoCard highlight={systemHealth?.overall_status === 'degraded'}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <NeonIcon icon={Heart} neonColor={systemHealth?.overall_status === 'healthy' ? 'green' : 'red'} size="w-8 h-8" />
                        <div>
                            <h3 className="font-bold text-white">{isSpanish ? 'Estado General del Sistema' : 'Overall System Status'}</h3>
                            <p className={`text-sm capitalize ${getStatusColor(systemHealth?.overall_status)}`}>{systemHealth?.overall_status || 'Loading...'}</p>
                        </div>
                    </div>
                    <button onClick={fetchSystemHealth} disabled={healthLoading} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                        <RefreshCw className={`w-5 h-5 text-white/70 ${healthLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                {systemHealth?.timestamp && <p className="text-xs text-white/40 mt-2">{isSpanish ? 'Última verificación:' : 'Last check:'} {new Date(systemHealth.timestamp).toLocaleString()}</p>}
            </BentoCard>
            
            <div className="flex gap-1 bg-black/30 rounded-lg p-1 overflow-x-auto">
                {[
                    { id: 'overview', label: isSpanish ? '📊 General' : '📊 Overview' },
                    { id: 'api', label: isSpanish ? '⚡ API' : '⚡ API' },
                    { id: 'jobs', label: isSpanish ? '⏰ Jobs' : '⏰ Jobs' },
                    { id: 'cache', label: isSpanish ? '💾 Cache' : '💾 Cache' },
                    { id: 'errors', label: isSpanish ? '🚨 Errors' : '🚨 Errors' },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
                        className={`flex-1 py-2 px-3 rounded-md text-xs transition-colors whitespace-nowrap ${activeSubTab === tab.id ? 'bg-primary/20 text-primary' : 'text-white/50 hover:text-white'}`}>
                        {tab.label}
                    </button>
                ))}
            </div>
            
            {healthLoading && !systemHealth ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
            ) : (
                <>
                    {activeSubTab === 'overview' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={Database} neonColor="cyan" size="w-6 h-6" /><h4 className="font-medium text-white">Database (Firestore)</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.database?.status)}<span className={getStatusColor(systemHealth?.services?.database?.status)}>{systemHealth?.services?.database?.status || 'Unknown'}</span></div>
                                {systemHealth?.services?.database?.users_count !== undefined && <p className="text-xs text-white/50 mt-2">{systemHealth.services.database.users_count} users in database</p>}
                            </BentoCard>
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={Cpu} neonColor="purple" size="w-6 h-6" /><h4 className="font-medium text-white">Gemini AI</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.gemini?.status)}<span className={getStatusColor(systemHealth?.services?.gemini?.status)}>{systemHealth?.services?.gemini?.status || 'Unknown'}</span></div>
                            </BentoCard>
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={Mail} neonColor="lime" size="w-6 h-6" /><h4 className="font-medium text-white">Resend (Email)</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.resend?.status)}<span className={getStatusColor(systemHealth?.services?.resend?.status)}>{systemHealth?.services?.resend?.status || 'Unknown'}</span></div>
                                <p className="text-xs text-white/50 mt-2">oracle@ • support@ • orbit@</p>
                            </BentoCard>
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={Shield} neonColor="orange" size="w-6 h-6" /><h4 className="font-medium text-white">Firebase Auth</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.firebase_auth?.status)}<span className={getStatusColor(systemHealth?.services?.firebase_auth?.status)}>{systemHealth?.services?.firebase_auth?.status || 'Unknown'}</span></div>
                            </BentoCard>
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={Bell} neonColor="yellow" size="w-6 h-6" /><h4 className="font-medium text-white">Push Notifications</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.push_notifications?.status)}<span className={getStatusColor(systemHealth?.services?.push_notifications?.status)}>{systemHealth?.services?.push_notifications?.status || 'Unknown'}</span></div>
                                {systemHealth?.services?.push_notifications?.registered_devices !== undefined && <p className="text-xs text-white/50 mt-2">{systemHealth.services.push_notifications.registered_devices} devices</p>}
                            </BentoCard>
                            <BentoCard>
                                <div className="flex items-center gap-3 mb-3"><NeonIcon icon={MapPin} neonColor="pink" size="w-6 h-6" /><h4 className="font-medium text-white">Google Places</h4></div>
                                <div className="flex items-center gap-2">{getStatusIcon(systemHealth?.services?.google_places?.status || 'configured')}<span className={getStatusColor(systemHealth?.services?.google_places?.status || 'configured')}>{systemHealth?.services?.google_places?.status || 'Configured'}</span></div>
                            </BentoCard>
                        </div>
                    )}
                    
                    {activeSubTab === 'api' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={Activity} neonColor="cyan" size="w-5 h-5" />{isSpanish ? 'Latencia de API' : 'API Latency'}</h3>
                                <button onClick={checkApiLatency} disabled={latencyLoading} className="px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors flex items-center gap-2 text-sm">
                                    {latencyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{isSpanish ? 'Verificar Ahora' : 'Check Now'}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-white/50 text-xs uppercase border-b border-white/10"><th className="text-left py-3">Endpoint</th><th className="text-center py-3">{isSpanish ? 'Estado' : 'Status'}</th><th className="text-center py-3">{isSpanish ? 'Latencia' : 'Latency'}</th><th className="text-right py-3">{isSpanish ? 'Última Verificación' : 'Last Check'}</th></tr></thead>
                                    <tbody>
                                        {Object.entries(apiLatency).length > 0 ? Object.entries(apiLatency).map(([endpoint, data]) => (
                                            <tr key={endpoint} className="border-t border-white/5">
                                                <td className="py-3 text-white font-mono text-xs">{endpoint}</td>
                                                <td className="py-3 text-center"><span className={`px-2 py-1 rounded text-xs ${data.status === 'healthy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{data.status}</span></td>
                                                <td className="py-3 text-center"><span className={`font-bold ${data.latency < 200 ? 'text-green-400' : data.latency < 500 ? 'text-yellow-400' : 'text-red-400'}`}>{data.latency ? `${data.latency}ms` : '—'}</span></td>
                                                <td className="py-3 text-right text-white/40 text-xs">{data.timestamp ? new Date(data.timestamp).toLocaleTimeString() : '—'}</td>
                                            </tr>
                                        )) : (<tr><td colSpan={4} className="py-8 text-center text-white/40">{isSpanish ? 'Haz clic en "Verificar Ahora" para medir latencia' : 'Click "Check Now" to measure latency'}</td></tr>)}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex gap-4 text-xs text-white/50">
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> &lt;200ms (Fast)</span>
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> 200-500ms (OK)</span>
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> &gt;500ms (Slow)</span>
                            </div>
                        </div>
                    )}
                    
                    {activeSubTab === 'jobs' && (
                        <div className="space-y-4">
                            <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={Clock} neonColor="magenta" size="w-5 h-5" />{isSpanish ? 'Cloud Scheduler Jobs' : 'Scheduled Jobs'}</h3>
                            {jobsLoading ? (<div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="text-white/50 text-xs uppercase border-b border-white/10"><th className="text-left py-3">Job</th><th className="text-center py-3">{isSpanish ? 'Estado' : 'Status'}</th><th className="text-center py-3">{isSpanish ? 'Última Ejecución' : 'Last Run'}</th><th className="text-center py-3">{isSpanish ? 'Próxima' : 'Next Run'}</th><th className="text-right py-3">{isSpanish ? 'Acción' : 'Action'}</th></tr></thead>
                                        <tbody>
                                            {scheduledJobs.length > 0 ? scheduledJobs.map((job) => (
                                                <tr key={job.id} className="border-t border-white/5">
                                                    <td className="py-3"><div className="flex items-center gap-2"><span className="text-lg">{job.icon || '⏰'}</span><div><p className="text-white font-medium">{job.name}</p><p className="text-xs text-white/40">{job.schedule}</p></div></div></td>
                                                    <td className="py-3 text-center"><span className={`px-2 py-1 rounded text-xs ${job.status === 'success' || job.status === 'enabled' ? 'bg-green-500/20 text-green-400' : job.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{job.status}</span></td>
                                                    <td className="py-3 text-center text-white/70 text-xs">{job.last_run ? new Date(job.last_run).toLocaleString() : '—'}</td>
                                                    <td className="py-3 text-center text-white/70 text-xs">{job.next_run || '—'}</td>
                                                    <td className="py-3 text-right"><button onClick={() => handleTriggerJob(job.id)} disabled={triggeringJob[job.id]} className="px-3 py-1 bg-primary/20 text-primary rounded text-xs hover:bg-primary/30 transition-colors disabled:opacity-50">{triggeringJob[job.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : (isSpanish ? 'Ejecutar' : 'Run Now')}</button></td>
                                                </tr>
                                            )) : (<tr><td colSpan={5} className="py-8 text-center text-white/40">{isSpanish ? 'No hay jobs configurados' : 'No scheduled jobs configured'}</td></tr>)}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p className="text-xs text-white/40">{isSpanish ? 'Los jobs se ejecutan automáticamente según su horario. Usa "Ejecutar" para pruebas manuales.' : 'Jobs run automatically on schedule. Use "Run Now" for manual testing.'}</p>
                        </div>
                    )}
                    
                    {activeSubTab === 'cache' && (
                        <div className="space-y-4">
                            <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={Database} neonColor="blue" size="w-5 h-5" />{isSpanish ? 'Gestión de Cache' : 'Cache Management'}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {cacheStats?.caches ? Object.entries(cacheStats.caches).map(([name, data]) => (
                                    <BentoCard key={name}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-white capitalize">{name.replace(/_/g, ' ')}</h4>
                                            <button onClick={() => handleClearCache(name)} disabled={clearingCache[name]} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30 transition-colors disabled:opacity-50">
                                                {clearingCache[name] ? <Loader2 className="w-3 h-3 animate-spin" /> : (isSpanish ? 'Limpiar' : 'Clear')}
                                            </button>
                                        </div>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between"><span className="text-white/50">{isSpanish ? 'Entradas' : 'Entries'}</span><span className="text-white font-bold">{data.count || 0}</span></div>
                                            {data.hit_rate !== undefined && <div className="flex justify-between"><span className="text-white/50">{isSpanish ? 'Tasa de Acierto' : 'Hit Rate'}</span><span className={`font-bold ${data.hit_rate >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>{data.hit_rate}%</span></div>}
                                            {data.last_cleared && <div className="flex justify-between"><span className="text-white/50">{isSpanish ? 'Última Limpieza' : 'Last Cleared'}</span><span className="text-white/70 text-xs">{new Date(data.last_cleared).toLocaleDateString()}</span></div>}
                                        </div>
                                    </BentoCard>
                                )) : ['horoscope', 'chart', 'energy'].map(name => (
                                    <BentoCard key={name}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-white capitalize">{name} Cache</h4>
                                            <button onClick={() => handleClearCache(name)} disabled={clearingCache[name]} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30 transition-colors disabled:opacity-50">
                                                {clearingCache[name] ? <Loader2 className="w-3 h-3 animate-spin" /> : (isSpanish ? 'Limpiar' : 'Clear')}
                                            </button>
                                        </div>
                                        <p className="text-sm text-white/50">{isSpanish ? 'Haz clic para limpiar' : 'Click to clear'}</p>
                                    </BentoCard>
                                ))}
                            </div>
                            <div className="pt-4 border-t border-white/10">
                                <button onClick={() => handleClearCache('all')} disabled={clearingCache['all']} className="w-full py-3 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2">
                                    {clearingCache['all'] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}{isSpanish ? 'Limpiar Todos los Caches' : 'Clear All Caches'}
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {activeSubTab === 'errors' && (
                        <div className="space-y-4">
                            <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={AlertTriangle} neonColor="red" size="w-5 h-5" />{isSpanish ? 'Dashboard de Errores' : 'Error Dashboard'}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <BentoCard><div className="text-center"><p className="text-3xl font-bold text-white">{errorStats?.total_24h || 0}</p><p className="text-sm text-white/50">{isSpanish ? 'Errores (24h)' : 'Errors (24h)'}</p></div></BentoCard>
                                <BentoCard><div className="text-center"><p className={`text-3xl font-bold ${(errorStats?.error_rate || 0) < 1 ? 'text-green-400' : (errorStats?.error_rate || 0) < 5 ? 'text-yellow-400' : 'text-red-400'}`}>{errorStats?.error_rate?.toFixed(2) || '0.00'}%</p><p className="text-sm text-white/50">{isSpanish ? 'Tasa de Error' : 'Error Rate'}</p></div></BentoCard>
                                <BentoCard><div className="text-center"><p className="text-3xl font-bold text-white">{errorStats?.unique_types || 0}</p><p className="text-sm text-white/50">{isSpanish ? 'Tipos Únicos' : 'Unique Types'}</p></div></BentoCard>
                            </div>
                            {errorStats?.top_errors && errorStats.top_errors.length > 0 && (
                                <BentoCard>
                                    <h4 className="font-medium text-white mb-3">{isSpanish ? 'Errores Más Frecuentes' : 'Most Frequent Errors'}</h4>
                                    <div className="space-y-2">{errorStats.top_errors.map((err, idx) => (<div key={idx} className="flex items-center justify-between p-2 bg-red-500/10 rounded-lg"><span className="text-sm text-white/80 truncate flex-1">{err.message}</span><span className="text-sm text-red-400 font-bold ml-2">{err.count}</span></div>))}</div>
                                </BentoCard>
                            )}
                            <BentoCard>
                                <h4 className="font-medium text-white mb-3">{isSpanish ? 'Errores Recientes' : 'Recent Errors'}</h4>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {errorStats?.recent && errorStats.recent.length > 0 ? errorStats.recent.map((err, idx) => (
                                        <div key={idx} className="p-3 bg-white/5 rounded-lg">
                                            <div className="flex items-center justify-between mb-1"><span className="text-xs text-red-400 uppercase">{err.type || 'Error'}</span><span className="text-xs text-white/30">{err.timestamp ? new Date(err.timestamp).toLocaleString() : '-'}</span></div>
                                            <p className="text-sm text-white/70 line-clamp-2">{err.message}</p>
                                            {err.endpoint && <p className="text-xs text-white/40 mt-1">Endpoint: {err.endpoint}</p>}
                                        </div>
                                    )) : (<p className="text-center text-white/40 py-4">{isSpanish ? '🎉 No hay errores recientes!' : '🎉 No recent errors!'}</p>)}
                                </div>
                            </BentoCard>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SystemHealthTab;
